import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  announceMemoAccepted,
  OPEN_MEMO_READER,
  type OpenMemoReaderDetail,
} from '../../common/memoReader'
import { useAuth } from '../../contexts/AuthContext'
import { MemoReadModal } from './MemoReadModal'

/**
 * The one memo reader. Anything opens it with openMemoReader(memo, email), and the
 * memo is read and acknowledged in place without navigating away.
 */
export function MemoReaderHost() {
  const { user } = useAuth()
  const [reading, setReading] = useState<OpenMemoReaderDetail | null>(null)
  const [open, setOpen] = useState(false)
  // Each open gets a fresh reader, even for the same memo.
  const [session, setSession] = useState(0)

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenMemoReaderDetail | undefined>).detail
      if (!detail?.memo?.memoId || !detail.email) return
      setReading(detail)
      setSession((current) => current + 1)
      setOpen(true)
    }
    window.addEventListener(OPEN_MEMO_READER, onOpen)
    return () => window.removeEventListener(OPEN_MEMO_READER, onOpen)
  }, [])

  const close = useCallback(() => setOpen(false), [])

  const accepted = (junctionId: string) => {
    close()
    announceMemoAccepted(junctionId)
    // In View As the memo is acknowledged for the person being viewed.
    const viewingAs = reading && reading.email.toLowerCase() !== user?.email?.toLowerCase()
    toast.success(viewingAs ? `Memo acknowledged for ${reading.email}` : 'Memo acknowledged')
  }

  if (!reading) return null
  return (
    <MemoReadModal
      key={session}
      memo={reading.memo}
      email={reading.email}
      open={open}
      onClose={close}
      onAccepted={accepted}
    />
  )
}
